import {Injectable} from '@angular/core';
import {AbstractService} from './abstract.service';
import {ApiVersion} from '../constants/apis';
import {Policy} from '../models/retention.model';
import {DateTime, Interval} from 'luxon';
import {RetentionImportStatistics} from '../models/retention-api-response';
import {codesExportFileHeaders, policyExportHeaders, policyExportKeys, retentionCodeKeys} from '../constants/retention-constants';
import {Observable} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class RetentionService extends AbstractService {

  constructor(private http: HttpClient) {
    super();
  }

  create(policy: Policy): Observable<{id: string}> {
    const url = this.toFinraApiServiceUrl(ApiVersion.V1, 'policy');
    return this.http.post<{id: string}>(url, policy);
  }

  listPolicies(type?: string, active?: boolean): Observable<Policy[]> {
    const url = this.toFinraApiServiceUrl(ApiVersion.V1, 'policy');
    let params = new HttpParams();
    if (type) {
      params = params.set('type', type);
    }
    if (active !== undefined) {
      params = params.set('active', String(active));
    }
    return this.http.get<Policy[]>(url, {params});
  }

  update(policyId: string, editRequest: Partial<Policy>): Observable<{policyId: string}> {
    const url = this.toFinraApiServiceUrl(ApiVersion.V1, 'policy/' + policyId);
    return this.http.patch<{policyId: string}>(url, editRequest);
  }

  delete(policyId: string): Observable<void> {
    const url = this.toFinraApiServiceUrl(ApiVersion.V1, 'policy/' + policyId);
    return this.http.delete<void>(url);
  }

  convertArrayOfObjectsToCSV(args: {}, keys: string[] = retentionCodeKeys, headers: string[] = codesExportFileHeaders): string {
    let result: string;
    let ctr: number;
    let columnDelimiter: string;
    let lineDelimiter: string;
    let policyCodes: string[];

    policyCodes = Object['values'](args['data']) || null;

    columnDelimiter = ',';
    lineDelimiter = '\n';
    result = headers.join(columnDelimiter) + lineDelimiter;

    if (policyCodes === null || !policyCodes.length) {
      return result;
    }

    policyCodes.forEach((item) => {
      ctr = 0;
      keys.forEach((key) => {
        if (ctr > 0) {
          result += columnDelimiter;
        }
        result += item[key] === undefined || item[key] === null ? '' : '"' + item[key] + '"';
        ctr++;
      });
      result += lineDelimiter;
    });

    return result;
  }

  isInQuarterInterval(date: DateTime): boolean {
    const startFirstInterval = DateTime.local(date.year, 3, 30, 0, 0, 0);
    const endFirstInterval = DateTime.local(date.year, 4, 10, 23, 59, 59);

    const startSecondInterval = DateTime.local(date.year, 6, 30, 23, 59, 59);
    const endSecondInterval = DateTime.local(date.year, 7, 10, 0, 0, 0);

    const startThirdInterval = DateTime.local(date.year, 9, 30, 0, 0, 0);
    const endThirdInterval = DateTime.local(date.year, 10, 10, 23, 59, 59);

    const startFourthInterval = DateTime.local(date.year, 12, 30, 0, 0, 0);
    const endFourthInterval = DateTime.local(date.year + 1, 1, 10, 23, 59, 59);

    const firstInterval = Interval.fromDateTimes(startFirstInterval, endFirstInterval);
    const secondInterval = Interval.fromDateTimes(startSecondInterval, endSecondInterval);
    const thirdInterval = Interval.fromDateTimes(startThirdInterval, endThirdInterval);
    const fourthInterval = Interval.fromDateTimes(startFourthInterval, endFourthInterval);

    return firstInterval.contains(date) || secondInterval.contains(date) || thirdInterval.contains(date) || fourthInterval.contains(date);
  }
}
